import React, { useEffect, useState } from 'react';
import {
  bonoDeReferidoPara,
  META_MENSUAL_BONO,
  META_MENSUAL_VIAJES,
  HITO_BONO,
  HITO_VIAJES,
} from '../constants/incentivos';
import { View, Text, StyleSheet, Modal, TouchableOpacity, ScrollView, Share, Alert, Platform, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { useThemeColors } from '../constants/theme';
import { useAppStore } from '../store/useAppStore';
import { firestoreService } from '../services/firestoreService';
import { RemoteConfigService } from '../services/remoteConfig';
import { pesos } from '../utils/dinero';

interface RewardsModalProps {
  visible: boolean;
  onClose: () => void;
  /**
   * El vehiculo REAL del chofer, no uno supuesto.
   *
   * ⚠️ FALTABA 'BICYCLE', y no era un descuido inofensivo: el servidor le paga
   * 800 a la bicicleta igual que a la moto (functions/src/incentives.ts), asi
   * que un repartidor en bici habria leido 1.000 y cobrado 800 aunque se le
   * pasara su vehiculo de verdad. Se abre a `string` para que ningun vehiculo
   * futuro se quede fuera por el tipo: `bonoDeReferidoPara` ya cae al valor por
   * defecto cuando no lo conoce, igual que el servidor.
   */
  vehicleType?: string;
}

export default function RewardsModal({ visible, onClose, vehicleType }: RewardsModalProps) {
  const colors = useThemeColors();
  const router = useRouter();
  const role = useAppStore((state) => state.userRole);
  const driverTrialDaysLeft = useAppStore((state) => state.driverTrialDaysLeft);
  const driverPaymentAccount = useAppStore((state) => state.driverPaymentAccount);

  /**
   * ── LOS NÚMEROS SALEN DE FIRESTORE, NO DEL STORE (27-sep-2026) ──────────
   *
   * Aquí se leían `driverTripsThisMonth`, `driverTotalLifetimeTrips`,
   * `driverBonusAccrued`, `passengerTripsCompleted` y `freeTripsAvailable` del
   * store. NINGUNO de los cinco lo escribe nadie en toda la app: nacen en 0 y
   * se quedan en 0. Así que esta pantalla le prometía dinero a la gente y le
   * enseñaba cero — a un chofer con viajes hechos y bonos ya acreditados por
   * `functions/src/incentives.ts`, y a un pasajero con viajes gratis ganados.
   *
   * Ahora se leen de verdad al ABRIR la pantalla (`visible`). Mientras llegan
   * no se pinta 0: se pintan rayas, porque un 0 se lee como un hecho.
   */
  // EL CODIGO DE REFERIDO, PEDIDO AL ABRIR Y NO AL PULSAR.
  // `referralService.getMyCode()` va a propósito SIN caché de módulo: un caché
  // sobrevivía al cambio de usuario en el mismo teléfono y el segundo acababa
  // compartiendo el código del primero. Esa decisión es buena y no se toca.
  // Lo que se arregla es CUÁNDO se pide: antes salía al pulsar "Invitar", y el
  // botón se quedaba mudo dos viajes de red (la función del servidor y la
  // dirección de la tienda) antes de abrir nada. Ahora se pide al abrir la
  // pantalla y se guarda JUNTO AL uid dueño: si al compartir el uid ya no
  // coincide, se vuelve a pedir. Se mantiene la garantía y se va la espera.
  const [codigoRef, setCodigoRef] = useState<{ uid: string; code: string } | null>(null);
  const [compartiendo, setCompartiendo] = useState(false);

  useEffect(() => {
    if (!visible) return;
    const uid = useAppStore.getState().userFirebaseUid;
    if (!uid) return;
    let vivo = true;
    import('../services/referralService')
      .then(({ referralService }) => referralService.getMyCode())
      .then((code) => { if (vivo && code) setCodigoRef({ uid, code }); })
      .catch(() => { /* sin red: handleShare lo reintenta al pulsar */ });
    return () => { vivo = false; };
  }, [visible]);

  const [recompensas, setRecompensas] = useState<{
    passengerTripsCompleted: number;
    freeTripsAvailable: number;
    driverTripsThisMonth: number;
    driverTotalLifetimeTrips: number;
    bonusAccrued: number;
  } | null>(null);
  // Arranca en «cargando» a propósito: así el efecto no tiene que fijar estado
  // de forma síncrona (react-hooks/set-state-in-effect) y la primera pintada ya
  // dice la verdad — que todavía no sabemos, no que sea cero.
  const [concursos, setConcursos] = useState<any[]>([]);
  const [cargandoRecompensas, setCargandoRecompensas] = useState(true);
  const [falloLaLectura, setFalloLaLectura] = useState(false);

  useEffect(() => {
    if (!visible) return;
    let vivo = true;
    const uid = useAppStore.getState().userFirebaseUid;
    const lectura = uid
      ? firestoreService.getRewardsSnapshot(uid)
      : Promise.resolve(null);
    // Los concursos abiertos van en la misma apertura de pantalla: si fallan,
    // no se cae el resto (se quedan en lista vacía y no se pinta la sección).
    firestoreService
      .getConcursosActivos()
      .then((cs) => { if (vivo) setConcursos(Array.isArray(cs) ? cs : []); })
      .catch(() => { if (vivo) setConcursos([]); });
    lectura
      .then((r) => {
        if (!vivo) return;
        if (r) {
          setRecompensas(r);
          setFalloLaLectura(false);
        } else {
          setFalloLaLectura(true);
        }
      })
      .catch(() => { if (vivo) setFalloLaLectura(true); })
      .finally(() => { if (vivo) setCargandoRecompensas(false); });
    return () => { vivo = false; };
  }, [visible]);

  const driverTripsThisMonth = recompensas?.driverTripsThisMonth ?? 0;
  const driverTotalLifetimeTrips = recompensas?.driverTotalLifetimeTrips ?? 0;
  const driverBonusAccrued = recompensas?.bonusAccrued ?? 0;
  const passengerTripsCompleted = recompensas?.passengerTripsCompleted ?? 0;
  const freeTripsAvailable = recompensas?.freeTripsAvailable ?? 0;
  /** Un número solo se enseña cuando se leyó. Si no, una raya. */
  const cifra = (n: number) => (recompensas ? String(n) : '—');

  const PASSENGER_TARGET = 10;
  const MONTHLY_TARGET = META_MENSUAL_VIAJES;
  const LIFETIME_TARGET = HITO_VIAJES;
  
  // ⚠️ ESTE MONTO LO LEE LA MISMA TABLA QUE USA EL SERVIDOR, Y NO UN BOOLEANO.
  //
  // Antes era `isMotorcycle ? 800 : 1000`, y fallaba por dos sitios a la vez:
  //  · `profile.tsx` montaba este modal con `vehicleType="CAR"` escrito a mano,
  //    asi que a TODO chofer se le prometian RD$1.000.
  //  · Y la BICICLETA no estaba contemplada: el servidor le paga 800.
  // Un motorista leia «Gana RD$1,000.00 por cada 3 choferes que invites»,
  // invitaba a tres, y le entraban 800.
  //
  // `bonoDeReferidoPara` es el espejo de `referralBonusFor` del servidor: misma
  // tabla, mismo valor por defecto. Ver constants/incentivos.ts.
  const REFERRAL_BONUS = bonoDeReferidoPara(vehicleType);
  const MONTHLY_BONUS = META_MENSUAL_BONO;
  const LIFETIME_BONUS = HITO_BONO;

  const handleShare = async () => {
    try {
      // ⚠️ Aquí había un `userId = "USER789"` escrito a mano: TODOS los usuarios
      // compartían el mismo código de referido, así que ninguna invitación se
      // podía acreditar y el bono por invitar choferes era imposible de ganar.
      const uid = useAppStore.getState().userFirebaseUid;
      if (!uid) {
        Alert.alert(
          'Inicia sesión primero',
          'Para compartir tu código de invitación necesitas tener tu cuenta abierta.'
        );
        return;
      }
      // Código de referido REAL y reversible (referral_codes → uid). Es el que
      // el backend sabe canjear. Si falla (sin red), generateShareMessage cae al
      // código legacy de solo-display.
      setCompartiendo(true);
      // Si ya lo trajimos al abrir la pantalla Y es de ESTE usuario, se usa tal
      // cual y la hoja de compartir abre al instante. Si no, se pide ahora.
      let realCode = codigoRef?.uid === uid ? codigoRef.code : null;
      if (!realCode) {
        const { referralService } = await import('../services/referralService');
        realCode = await referralService.getMyCode();
        if (realCode) setCodigoRef({ uid, code: realCode });
      }
      const dynamicMessage = await RemoteConfigService.generateShareMessage(
        uid,
        role || 'PASSENGER',
        realCode || undefined
      );

      // 2. Abrimos la hoja de compartir nativa de iOS/Android
      await Share.share({
        message: dynamicMessage,
      });
    } catch (error) {
      console.error(error);
      Alert.alert("Error", "No se pudo compartir el enlace.");
    } finally {
      setCompartiendo(false);
    }
  };

  const renderPassengerView = () => (
    <View style={styles.passengerContainer}>
      <View style={styles.giftCircle}>
        <Ionicons name="gift" size={50} color="#FFF" />
      </View>
      <Text style={[styles.mainTitle, { color: colors.text }]}>Programa de Lealtad</Text>
      <Text style={[styles.subDesc, { color: colors.text }]}>
        ¡Viaja 10 veces y el próximo es por la casa! Además, invita amigos: cuando quien invitaste complete sus primeros 10 viajes, ganan un viaje gratis LOS DOS.
      </Text>

      {/* Passenger Progress */}
      <View style={[styles.progressCard, { backgroundColor: colors.card, width: '100%', marginBottom: 20 }]}>
        <View style={styles.progressHeader}>
          <Text style={[styles.progressTitle, { color: colors.text }]}>Hacia tu Viaje Gratis</Text>
          <Text style={styles.progressAmount}>{cifra(passengerTripsCompleted)}/{PASSENGER_TARGET}</Text>
        </View>
        <View style={styles.progressBarBg}>
          <View style={[styles.progressBarFill, { backgroundColor: '#FF3B30', width: `${Math.min((passengerTripsCompleted / PASSENGER_TARGET) * 100, 100)}%` }]} />
        </View>
        <View style={styles.progressFooter}>
          <Text style={[styles.progressStatus, { color: colors.textSecondary }]}>
            {recompensas ? `Solo te faltan ${PASSENGER_TARGET - passengerTripsCompleted} viajes.` : 'Cargando tus viajes…'}
          </Text>
        </View>
      </View>

      <View style={[styles.couponCard, { backgroundColor: colors.card }]}>
        <Text style={[styles.couponTitle, { color: colors.text }]}>Cupones Disponibles</Text>
        <Text style={styles.couponCount}>{cifra(freeTripsAvailable)}</Text>
      </View>

      <TouchableOpacity
        style={[styles.shareButton, compartiendo && { opacity: 0.7 }]}
        onPress={handleShare}
        disabled={compartiendo}
      >
        {compartiendo
          ? <ActivityIndicator size="small" color="#FFF" />
          : <Ionicons name="share-social-outline" size={24} color="#FFF" />}
        <Text style={styles.shareButtonText}>
          {compartiendo ? 'Preparando…' : 'Invitar Amigos Ahora'}
        </Text>
      </TouchableOpacity>
      {codigoRef?.code ? (
        <Text style={{ color: colors.textSecondary, fontSize: 13, marginTop: 10, textAlign: 'center' }}>
          Tu código de invitación:{' '}
          <Text style={{ fontWeight: '800', color: colors.text, letterSpacing: 1 }}>{codigoRef.code}</Text>
        </Text>
      ) : null}

    </View>
  );

  /**
   * LOS CONCURSOS ABIERTOS. Los crea el fundador desde su panel y lo que se
   * pinta aquí sale tal cual de Firestore: título, qué se mide, la meta y el
   * premio en saldo. Si no hay ninguno, esta sección no existe — una tarjeta
   * vacía que diga «no hay concursos» solo ocupa sitio.
   */
  const renderConcursos = () => {
    if (!concursos.length) return null;
    return (
      <View style={{ marginBottom: 16 }}>
        {concursos.map((c: any) => {
          const queMide =
            String(c?.metrica ?? '') === 'INVITACIONES'
              ? 'choferes invitados que paguen su cuota'
              : 'viajes completados';
          const finMs = c?.fin?.toMillis?.() ?? c?.fin?.toDate?.()?.getTime?.() ?? 0;
          const dias = finMs ? Math.max(0, Math.ceil((finMs - Date.now()) / 86400000)) : null;
          return (
            <View
              key={String(c.id)}
              style={{
                backgroundColor: 'rgba(255,149,0,0.10)',
                borderColor: '#FF9500',
                borderWidth: 1.5,
                borderRadius: 14,
                padding: 14,
                marginBottom: 10,
              }}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 6 }}>
                <Ionicons name="trophy" size={18} color="#FF9500" />
                <Text style={{ color: '#FF9500', fontWeight: '900', fontSize: 15, marginLeft: 8, flex: 1 }}>
                  {String(c?.titulo ?? 'Concurso')}
                </Text>
              </View>
              {!!c?.descripcion && (
                <Text style={{ color: colors.text, fontSize: 13, marginBottom: 6 }}>{String(c.descripcion)}</Text>
              )}
              <Text style={{ color: colors.textSecondary, fontSize: 13 }}>
                {`Meta: ${Number(c?.meta ?? 0)} ${queMide}. Premio: ${pesos(Number(c?.premio ?? 0))} de saldo.`}
              </Text>
              {dias !== null && (
                <Text style={{ color: colors.textSecondary, fontSize: 12, marginTop: 4 }}>
                  {dias === 0 ? 'Termina hoy.' : dias === 1 ? 'Queda 1 día.' : `Quedan ${dias} días.`}
                </Text>
              )}
            </View>
          );
        })}
      </View>
    );
  };

  const renderDriverView = () => (
    <View style={styles.driverContainer}>
      {/* Lo primero: lo que cambia esta semana. */}
      {renderConcursos()}
      
      {/* 15 Days Free Trial Banner */}
      {typeof driverTrialDaysLeft === 'number' && driverTrialDaysLeft > 0 && (
        <View style={styles.trialBanner}>
          <Ionicons name="flash" size={24} color="#FF9500" />
          <View style={styles.trialBannerTextContainer}>
            <Text style={styles.trialBannerTitle}>¡Prueba Gratis Activa!</Text>
            <Text style={styles.trialBannerDesc}>Te quedan {driverTrialDaysLeft} días para usar la app sin pagar cuota.</Text>
          </View>
        </View>
      )}

      {/* Monthly Goal Progress */}
      <View style={[styles.progressCard, { backgroundColor: colors.card }]}>
        <View style={styles.progressHeader}>
          <Text style={[styles.progressTitle, { color: colors.text }]}>Meta Mensual</Text>
          {/* Decía RD$ 400 escrito a mano cuando el servidor paga RD$ 500
              (functions/src/incentives.ts → MONTHLY_GOAL_BONUS). Prometerle a
              un chofer menos de lo que se le va a pagar es menos grave que al
              revés, pero igual es información falsa sobre su dinero. */}
          <Text style={[styles.progressAmount, { color: '#00D084' }]}>{pesos(MONTHLY_BONUS)}</Text>
        </View>
        <Text style={[styles.goalDesc, { color: colors.textSecondary }]}>Completa 250 viajes este mes.</Text>
        <View style={styles.progressBarBg}>
          <View style={[styles.progressBarFill, { backgroundColor: '#00D084', width: `${Math.min((driverTripsThisMonth / MONTHLY_TARGET) * 100, 100)}%` }]} />
        </View>
        <View style={styles.progressFooter}>
          <Text style={[styles.progressStatus, { color: colors.textSecondary }]}>
            {!recompensas
              ? `— / ${MONTHLY_TARGET} Viajes`
              : driverTripsThisMonth >= MONTHLY_TARGET ? '¡Meta Cumplida!' : `${driverTripsThisMonth} / ${MONTHLY_TARGET} Viajes`}
          </Text>
        </View>
      </View>

      {/* Lifetime Goal Progress */}
      <View style={[styles.progressCard, { backgroundColor: colors.card, marginTop: 15 }]}>
        <View style={styles.progressHeader}>
          <Text style={[styles.progressTitle, { color: colors.text }]}>Meta Diamante</Text>
          <Text style={[styles.progressAmount, { color: '#9C27B0' }]}>{pesos(LIFETIME_BONUS)}</Text>
        </View>
        <Text style={[styles.goalDesc, { color: colors.textSecondary }]}>Llega a 1,500 viajes en tu historia.</Text>
        <View style={styles.progressBarBg}>
          <View style={[styles.progressBarFill, { backgroundColor: '#9C27B0', width: `${Math.min((driverTotalLifetimeTrips / LIFETIME_TARGET) * 100, 100)}%` }]} />
        </View>
        <View style={styles.progressFooter}>
          <Text style={[styles.progressStatus, { color: colors.textSecondary }]}>
            {!recompensas
              ? `— / ${LIFETIME_TARGET} Viajes Totales`
              : driverTotalLifetimeTrips >= LIFETIME_TARGET ? '¡Meta Diamante Cumplida!' : `${driverTotalLifetimeTrips} / ${LIFETIME_TARGET} Viajes Totales`}
          </Text>
        </View>
      </View>

      {/* Total Accrued Bonus */}
      <View style={styles.payoutContainer}>
        <Text style={[styles.payoutLabel, { color: colors.text }]}>Bonos Generados (Referidos y Metas):</Text>
        <Text style={styles.payoutTotal}>{recompensas ? pesos(driverBonusAccrued) : '—'}</Text>
        {/* Si la lectura falló se DICE. Antes, cualquier fallo se veía igual
            que «no has ganado nada»: la misma pantalla para dos cosas que no
            se parecen en nada. */}
        {(falloLaLectura || cargandoRecompensas) && (
          <Text style={{ fontSize: 12, color: colors.textSecondary, marginTop: 4 }}>
            {cargandoRecompensas
              ? 'Cargando tus recompensas…'
              : 'No pudimos leer tus recompensas ahora. Revisa tu conexión y vuelve a abrir esta pantalla.'}
          </Text>
        )}
        <Text style={styles.payoutNote}>Fecha de corte y pago: Día 10 de cada mes</Text>
      </View>

      {/* ── EL BOTÓN «ABONAR A MI CUOTA» SE QUITÓ (27-sep-2026) ───────────────
          Enseñaba «Suscripción Pagada — tu bono ha sido transferido» y por
          dentro solo tocaba el store: NINGUNA llamada al servidor. Estaba
          muerto por accidente —solo se pintaba con `driverBonusAccrued > 0` y
          ese contador nunca salía de 0—, así que al cablear esta pantalla a los
          datos reales se habría vuelto alcanzable: un botón que dice que pagó
          algo y no paga nada, justo el patrón que Google Play cita como
          Broken Functionality (igual que el «Retirar a mi Cuenta» que ya se
          quitó por lo mismo).
          Y sobra: el bono ya se acredita SOLO. `pagarBono` (functions/src/
          incentives.ts) escribe el asiento en /driver_bonuses y, cuando el
          destino es la billetera, suma a `subscriptionCredit` en la misma
          transacción. Aquí solo hay que decirlo. */}
      {driverBonusAccrued > 0 && (
        <View style={styles.decisionContainer}>
          <Text style={[styles.decisionTitle, { color: colors.text }]}>Tus bonos se acreditan solos</Text>
          <Text style={[styles.decisionDesc, { color: colors.textSecondary }]}>
            Lo que ganas se descuenta de tu cuota mensual sin que tengas que hacer nada. El corte es
            el día 10 de cada mes.
          </Text>
        </View>
      )}

      {/* Payment Account Input */}
      <TouchableOpacity
        style={styles.paymentAccountBtn}
        onPress={() => {
          // ── AQUÍ LA APP MENTÍA ───────────────────────────────
          // En web se abría un prompt del navegador y lo escrito iba a
          // `setDriverPaymentAccount`, un campo de Zustand que NO se escribe en
          // Firestore por ninguna parte. La fila pasaba entonces a decir
          // «Registrado: …», así que el chofer quedaba convencido de que tenía su
          // cuenta puesta mientras `users/{uid}.bankAccounts` seguía sin existir
          // —y el pasajero, al ir a pagarle, no veía ninguna cuenta.
          // Se quita el prompt: las dos plataformas van a la pantalla de verdad,
          // que sí guarda en el servidor.
          // Guard de rol: /driver-bank-accounts es solo para choferes/repartidores.
          // renderDriverView se muestra para cualquier rol != PASSENGER (incluye
          // GUEST con uid rehidratado), así que validamos antes de navegar.
          if (role !== 'DRIVER' && role !== 'DELIVERY_DRIVER' && role !== 'SCHOOL_DRIVER') {
            Alert.alert('Solo para choferes', 'Esta opción está disponible para cuentas de chofer o repartidor.');
            return;
          }
          // Native (Android/iOS): window.prompt es undefined en Hermes y crashea.
          // Redirigimos a la pantalla dedicada que ya maneja el guardado.
          onClose();
          router.push('/driver-bank-accounts');
        }}
      >
        <Ionicons name="card-outline" size={24} color={colors.text} />
        <View style={styles.paymentAccountTexts}>
          <Text style={[styles.paymentAccountTitle, { color: colors.text }]}>Método de Recepción de Bonos</Text>
          <Text style={[styles.paymentAccountSubtitle, { color: colors.textSecondary }]}>
            {/* `driverPaymentAccount` vive SOLO en memoria: no es prueba de
                nada. La verdad de si hay cuenta está en el servidor, y esta
                fila no la consulta — así que no afirma. */}
            Agregar o ver mis cuentas bancarias
          </Text>
        </View>
        <Ionicons name="chevron-forward" size={20} color={colors.textSecondary} />
      </TouchableOpacity>

      <View style={{ backgroundColor: 'rgba(255, 149, 0, 0.1)', borderColor: 'rgba(255, 149, 0, 0.3)', borderWidth: 1, padding: 15, borderRadius: 15, marginTop: 20 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 5 }}>
          <Ionicons name="information-circle" size={20} color="#FF9500" />
          <Text style={{ fontSize: 15, fontWeight: 'bold', color: '#FF9500', marginLeft: 8 }}>¿Cómo funciona el Bono por Referidos?</Text>
        </View>
        <Text style={{ fontSize: 13, color: colors.textSecondary, lineHeight: 18 }}>
          Gana <Text style={{ fontWeight: 'bold', color: colors.text }}>{pesos(REFERRAL_BONUS)}</Text> por cada <Text style={{ fontWeight: 'bold', color: colors.text }}>3 choferes</Text> que invites con tu enlace. El bono se reflejará y pagará el <Text style={{ fontWeight: 'bold', color: colors.text }}>día 10 de cada mes</Text>, siempre y cuando los choferes invitados hayan realizado el pago de su suscripción mensual.
        </Text>
      </View>

      <TouchableOpacity
        style={[styles.shareButton, { marginTop: 15 }, compartiendo && { opacity: 0.7 }]}
        onPress={handleShare}
        disabled={compartiendo}
      >
        {compartiendo
          ? <ActivityIndicator size="small" color="#FFF" />
          : <Ionicons name="people-outline" size={24} color="#FFF" />}
        <Text style={styles.shareButtonText}>
          {compartiendo ? 'Preparando…' : `Invitar 3 Choferes (Gana ${pesos(REFERRAL_BONUS)})`}
        </Text>
      </TouchableOpacity>
      {codigoRef?.code ? (
        <Text style={{ color: colors.textSecondary, fontSize: 13, marginTop: 10, textAlign: 'center' }}>
          Tu código de invitación:{' '}
          <Text style={{ fontWeight: '800', color: colors.text, letterSpacing: 1 }}>{codigoRef.code}</Text>
        </Text>
      ) : null}

    </View>
  );

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet" onRequestClose={onClose}>
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <View style={styles.header}>
          <TouchableOpacity onPress={onClose} style={styles.closeButton}>
            <Ionicons name="chevron-down" size={28} color={colors.text} />
          </TouchableOpacity>
          <Text style={[styles.title, { color: colors.text }]}>
            {role === 'PASSENGER' ? 'Invita y Gana' : 'Mis Recompensas'}
          </Text>
          <View style={{ width: 28 }} />
        </View>

        <ScrollView contentContainerStyle={styles.content}>
          {role === 'PASSENGER' ? renderPassengerView() : renderDriverView()}
        </ScrollView>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#EEE',
  },
  closeButton: {
    padding: 5,
  },
  title: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  content: {
    padding: 20,
  },
  passengerContainer: {
    alignItems: 'center',
    paddingTop: 20,
  },
  giftCircle: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: '#FF3B30',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
    shadowColor: '#FF3B30',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.3,
    shadowRadius: 15,
    elevation: 8,
  },
  mainTitle: {
    fontSize: 26,
    fontWeight: '900',
    marginBottom: 10,
    textAlign: 'center',
  },
  subDesc: {
    fontSize: 16,
    textAlign: 'center',
    opacity: 0.8,
    lineHeight: 24,
    paddingHorizontal: 10,
    marginBottom: 30,
  },
  couponCard: {
    width: '100%',
    padding: 25,
    borderRadius: 20,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#EEE',
    marginBottom: 30,
  },
  couponTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 10,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  couponCount: {
    fontSize: 48,
    fontWeight: '900',
    color: '#FF3B30',
  },
  shareButton: {
    backgroundColor: '#0A2540',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 18,
    borderRadius: 15,
    width: '100%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 5,
    elevation: 5,
  },
  shareButtonText: {
    color: '#FFF',
    fontWeight: 'bold',
    fontSize: 18,
    marginLeft: 10,
  },
  driverContainer: {
    paddingTop: 10,
  },
  deadlineBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 149, 0, 0.1)',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 20,
    marginBottom: 20,
    alignSelf: 'center',
  },
  deadlineText: {
    color: '#FF9500',
    fontWeight: 'bold',
    fontSize: 12,
    marginLeft: 5,
  },
  progressCard: {
    padding: 20,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#EEE',
  },
  progressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginBottom: 15,
  },
  progressTitle: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  progressAmount: {
    fontSize: 24,
    fontWeight: '900',
    color: '#00D084',
  },
  progressBarBg: {
    height: 12,
    backgroundColor: '#F0F0F0',
    borderRadius: 6,
    overflow: 'hidden',
    marginBottom: 10,
  },
  progressBarFill: {
    height: '100%',
    backgroundColor: '#00D084',
    borderRadius: 6,
  },
  progressFooter: {
    alignItems: 'flex-end',
  },
  progressStatus: {
    fontSize: 14,
    fontWeight: '600',
    opacity: 0.8,
  },
  payoutContainer: {
    backgroundColor: '#E8F5E9',
    padding: 20,
    borderRadius: 15,
    marginTop: 25,
    marginBottom: 30,
    alignItems: 'center',
  },
  payoutLabel: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 5,
  },
  payoutTotal: {
    fontSize: 42,
    fontWeight: '900',
    color: '#0A2540',
    marginBottom: 10,
  },
  payoutNote: {
    fontSize: 12,
    color: '#0A2540',
    textAlign: 'center',
    opacity: 0.7,
  },
  trialBanner: {
    flexDirection: 'row',
    backgroundColor: 'rgba(255, 149, 0, 0.1)',
    borderWidth: 1,
    borderColor: 'rgba(255, 149, 0, 0.3)',
    borderRadius: 15,
    padding: 15,
    marginBottom: 20,
    alignItems: 'center',
  },
  trialBannerTextContainer: {
    marginLeft: 15,
    flex: 1,
  },
  trialBannerTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#FF9500',
    marginBottom: 4,
  },
  trialBannerDesc: {
    fontSize: 13,
    color: '#333',
    lineHeight: 18,
  },
  goalDesc: {
    fontSize: 13,
    marginBottom: 10,
  },
  paymentAccountBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 15,
    borderWidth: 1,
    borderColor: '#EEE',
    borderRadius: 15,
    marginTop: 10,
  },
  paymentAccountTexts: {
    flex: 1,
    marginLeft: 15,
  },
  paymentAccountTitle: {
    fontSize: 14,
    fontWeight: 'bold',
  },
  paymentAccountSubtitle: {
    fontSize: 12,
    marginTop: 2,
  },
  decisionContainer: {
    padding: 20,
    borderWidth: 1,
    borderColor: '#00D084',
    backgroundColor: 'rgba(0, 208, 132, 0.05)',
    borderRadius: 15,
    marginBottom: 20,
    alignItems: 'center',
  },
  decisionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 5,
  },
  decisionDesc: {
    fontSize: 14,
    marginBottom: 15,
    textAlign: 'center',
  },
  actionButtonPrimary: {
    backgroundColor: '#00D084',
    flexDirection: 'row',
    padding: 15,
    borderRadius: 10,
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 10,
  },
  actionButtonSecondary: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: '#0A2540',
    flexDirection: 'row',
    padding: 15,
    borderRadius: 10,
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  actionButtonText: {
    color: '#FFF',
    fontWeight: 'bold',
    marginLeft: 10,
  }
});
